/**
 * Toast Notification Service
 *
 * Centralised service for managing toast notifications across the application.
 * Integrates with react-hot-toast for consistent user feedback on API operations.
 */

import React from 'react';
import toast from 'react-hot-toast';

export interface ToastOptions {
  id?: string;
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  style?: React.CSSProperties;
  className?: string;
}

export interface ApiToastOptions extends ToastOptions {
  showLoading?: boolean;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

class ToastService {
  private loadingToasts = new Map<string, string>();

  /**
   * Show success toast
   */
  success(message: string, options: ToastOptions = {}): string {
    return toast.success(message, {
      duration: 4000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: 'white',
        fontWeight: '500',
      },
      ...options,
    });
  }

  /**
   * Show error toast
   */
  error(message: string, options: ToastOptions = {}): string {
    return toast.error(message, {
      duration: 6000,
      icon: '❌',
      style: {
        background: '#EF4444',
        color: 'white',
        fontWeight: '500',
      },
      ...options,
    });
  }

  /**
   * Show warning toast
   */
  warning(message: string, options: ToastOptions = {}): string {
    return toast(message, {
      duration: 5000,
      icon: '⚠️',
      style: {
        background: '#F59E0B',
        color: 'white',
        fontWeight: '500',
      },
      ...options,
    });
  }

  /**
   * Show purple progress toast (for "taking longer" notifications)
   */
  progress(message: string, options: ToastOptions = {}): string {
    return toast(message, {
      duration: 8000,
      icon: '⏳',
      style: {
        background: '#8B5CF6',
        color: 'white',
        fontWeight: '500',
      },
      ...options,
    });
  }

  /**
   * Show info toast
   */
  info(message: string, options: ToastOptions = {}): string {
    return toast(message, {
      duration: 4000,
      icon: 'ℹ️',
      style: {
        background: '#3B82F6',
        color: 'white',
        fontWeight: '500',
      },
      ...options,
    });
  }

  /**
   * Show loading toast
   */
  loading(message: string, options: ToastOptions = {}): string {
    return toast.loading(message, {
      icon: '⏳',
      style: {
        background: '#6366f1',
        color: 'white',
        fontWeight: '500',
      },
      ...options,
    });
  }

  /**
   * Dismiss a specific toast
   */
  dismiss(toastId?: string): void {
    toast.dismiss(toastId);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    toast.dismiss();
  }

  /**
   * Promise-based toast for API calls
   */
  async promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    },
    options: ToastOptions = {}
  ): Promise<T> {
    return toast.promise(promise, messages, {
      style: {
        fontWeight: '500',
      },
      success: {
        style: {
          background: '#10B981',
          color: 'white',
        },
        icon: '✅',
      },
      error: {
        style: {
          background: '#EF4444',
          color: 'white',
        },
        icon: '❌',
      },
      loading: {
        style: {
          background: '#6366f1',
          color: 'white',
        },
        icon: '⏳',
      },
      ...options,
    });
  }

  /**
   * API operation toast with automatic loading/success/error handling
   */
  async apiOperation<T>(
    operation: () => Promise<T>,
    operationId: string,
    options: ApiToastOptions = {}
  ): Promise<T> {
    const {
      showLoading = true,
      loadingMessage = 'Working on it...',
      successMessage = 'Done!',
      errorMessage = 'Something went wrong. Please try again.',
      ...toastOptions
    } = options;

    let loadingToastId: string | undefined;

    try {
      // Show loading toast if enabled
      if (showLoading) {
        loadingToastId = this.loading(loadingMessage, toastOptions);
        this.loadingToasts.set(operationId, loadingToastId);
      }

      // Execute the operation
      const result = await operation();

      // Dismiss loading toast and show success
      if (loadingToastId) {
        this.dismiss(loadingToastId);
        this.loadingToasts.delete(operationId);
      }

      this.success(successMessage, toastOptions);

      return result;
    } catch (error) {
      // Dismiss loading toast and show error
      if (loadingToastId) {
        this.dismiss(loadingToastId);
        this.loadingToasts.delete(operationId);
      }

      this.error(errorMessage, toastOptions);
      throw error;
    }
  }

  /**
   * Batch operation toast for multiple API calls
   */
  async batchOperation<T>(
    operations: Array<() => Promise<T>>,
    options: {
      loadingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
      showProgress?: boolean;
    } = {}
  ): Promise<T[]> {
    const {
      loadingMessage = 'Working on multiple items...',
      successMessage = 'All done!',
      errorMessage = 'Some items couldn\'t be processed',
      showProgress = true,
    } = options;

    const loadingToastId = this.loading(loadingMessage);
    const results: T[] = [];
    const errors: any[] = [];

    try {
      for (let i = 0; i < operations.length; i++) {
        try {
          if (showProgress) {
            toast.loading(`Processing ${i + 1}/${operations.length}...`, {
              id: loadingToastId,
            });
          }

          const result = await operations[i]();
          results.push(result);
        } catch (error) {
          errors.push(error);
        }
      }

      this.dismiss(loadingToastId);

      if (errors.length === 0) {
        this.success(successMessage);
      } else if (errors.length < operations.length) {
        this.warning(`${operations.length - errors.length} of ${operations.length} completed`);
      } else {
        this.error(errorMessage);
      }

      return results;
    } catch (error) {
      this.dismiss(loadingToastId);
      this.error(errorMessage);
      throw error;
    }
  }

  /**
   * Network error toast with retry option
   */
  networkError(
    message: string = 'Connection problem',
    retryFn?: () => void,
    options: ToastOptions = {}
  ): string {
    if (retryFn) {
      // Show error with instruction to retry manually
      return this.error(
        `${message}. Please try again.`,
        { duration: 8000, ...options }
      );
    }

    return this.error(message, { duration: 6000, ...options });
  }

  /**
   * Validation error toast
   */
  validationError(
    errors: string[] | string,
    options: ToastOptions = {}
  ): string {
    const message = Array.isArray(errors) 
      ? errors.length > 1 
        ? `Please fix ${errors.length} issues before continuing`
        : errors[0]
      : errors;

    return this.error(message, { duration: 5000, ...options });
  }

  /**
   * Permission denied toast
   */
  permissionDenied(
    message: string = 'You don\'t have access to do this',
    options: ToastOptions = {}
  ): string {
    return this.warning(message, { duration: 5000, ...options });
  }

  /**
   * Rate limit toast
   */
  rateLimit(
    message: string = 'Slow down! Please wait a moment before trying again.',
    options: ToastOptions = {}
  ): string {
    // Stable toast ID prevents stacking — new 429 replaces the existing toast
    return this.warning(message, { duration: 8000, ...options, id: 'rate-limit-429' } as any);
  }

  /**
   * Toast cooldown map — shared by upgrade + limit toasts.
   * Prevents spam from multiple 402/limit responses firing in quick succession.
   */
  private _upgradeToastCooldowns = new Map<string, number>();

  private _shouldShowUpgradeToast(featureKey: string): boolean {
    const now = Date.now();
    const lastShown = this._upgradeToastCooldowns.get(featureKey) ?? 0;
    if (now - lastShown < 10_000) return false;
    this._upgradeToastCooldowns.set(featureKey, now);
    return true;
  }

  /**
   * Feature not available on current plan (inactive subscription or plan doesn't include it).
   */
  upgradeRequired(
    featureName: string,
    options: ToastOptions = {}
  ): string {
    const featureKey = featureName.toLowerCase().replace(/\s+/g, '-');
    const toastId = `upgrade-required:${featureKey}`;
    if (!this._shouldShowUpgradeToast(featureKey)) return toastId;

    return toast(
      (t) => {
        const handleClick = () => { toast.dismiss(t.id); window.location.href = '/settings?tab=subscription'; };
        return (
          <span>
            {featureName} is not available on your current plan.{' '}
            <span onClick={handleClick} style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}>
              See upgrade options
            </span>
          </span>
        );
      },
      { ...options, id: toastId, duration: 6000, icon: '🔒', style: { background: '#7c3aed', color: 'white', fontWeight: '500' } }
    );
  }

  /**
   * Trial-layer lock (402 TRIAL_LOCKED): the server's own sentence, then the
   * upgrade link. WB-61: this went through upgradeRequired(), which reads its
   * argument as a feature NAME, so a lapsed user saw "<the whole sentence> is
   * not available on your current plan."
   */
  trialLocked(message: string, options: ToastOptions = {}): string {
    const toastId = 'trial-locked';
    if (!this._shouldShowUpgradeToast(toastId)) return toastId;

    return toast(
      (t) => {
        const handleClick = () => { toast.dismiss(t.id); window.location.href = '/settings?tab=subscription'; };
        return (
          <span>
            {message}{' '}
            <span onClick={handleClick} style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}>
              See upgrade options
            </span>
          </span>
        );
      },
      { ...options, id: toastId, duration: 6000, icon: '🔒', style: { background: '#7c3aed', color: 'white', fontWeight: '500' } }
    );
  }

  /**
   * Monthly plan limit reached (user has access, but has used their allowance).
   */
  planLimitReached(
    featureName: string,
    usage?: { current?: number; limit?: number },
    options: ToastOptions = {}
  ): string {
    const featureKey = featureName.toLowerCase().replace(/\s+/g, '-');
    const toastId = `limit-reached:${featureKey}`;
    if (!this._shouldShowUpgradeToast(featureKey)) return toastId;

    const usageText = usage?.current != null && usage?.limit != null
      ? ` (${usage.current}/${usage.limit} used this month)`
      : '';

    return toast(
      (t) => {
        const handleClick = () => { toast.dismiss(t.id); window.location.href = '/settings?tab=subscription'; };
        return (
          <span>
            {featureName} limit reached{usageText}.{' '}
            <span onClick={handleClick} style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}>
              Upgrade for more
            </span>
          </span>
        );
      },
      { ...options, id: toastId, duration: 6000, icon: '📊', style: { background: '#d97706', color: 'white', fontWeight: '500' } }
    );
  }

  /**
   * Out of AI tokens (402 INSUFFICIENT_TOKENS). User has used their monthly
   * AI token allowance — distinct from a feature lock or a per-feature cap.
   */
  outOfTokens(
    details?: { required?: number; available?: number; limit?: number },
    options: ToastOptions = {}
  ): string {
    const toastId = 'out-of-tokens';
    if (!this._shouldShowUpgradeToast('ai-tokens')) return toastId;

    const allowanceText =
      details?.limit != null
        ? ` Your monthly allowance of ${details.limit.toLocaleString()} tokens has run out.`
        : '';

    return toast(
      (t) => {
        const handleClick = () => { toast.dismiss(t.id); window.location.href = '/settings?tab=subscription'; };
        return (
          <span>
            You&apos;ve used all your AI tokens for this month.{allowanceText}{' '}
            <span onClick={handleClick} style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}>
              Upgrade for more
            </span>
          </span>
        );
      },
      { ...options, id: toastId, duration: 7000, icon: '🪫', style: { background: '#7c3aed', color: 'white', fontWeight: '500' } }
    );
  }

  /**
   * Maintenance mode toast
   */
  maintenance(
    message: string = 'We\'re doing some maintenance right now. Please try again shortly.',
    options: ToastOptions = {}
  ): string {
    return this.info(message, { duration: 8000, ...options });
  }

  /**
   * Progressive status updates for long-running operations
   */
  progressiveStatus(
    statusMessages: string[],
    options: {
      initialMessage?: string;
      finalMessage?: string;
      interval?: number;
      showFinalSuccess?: boolean;
    } = {}
  ): Promise<void> {
    const {
      initialMessage = 'Getting started...',
      finalMessage = 'All done!',
      interval = 1000,
      showFinalSuccess = true
    } = options;

    return new Promise((resolve) => {
      let currentIndex = 0;
      const toastId = this.loading(initialMessage);

      const updateStatus = () => {
        if (currentIndex < statusMessages.length) {
          const message = statusMessages[currentIndex];
          toast.loading(message, { id: toastId });
          currentIndex++;
          setTimeout(updateStatus, interval);
        } else {
          this.dismiss(toastId);
          if (showFinalSuccess) {
            this.success(finalMessage);
          }
          resolve();
        }
      };

      // Start the progressive updates
      setTimeout(updateStatus, interval);
    });
  }

  /**
   * Update an existing toast with new content
   */
  updateToast(toastId: string, message: string, type: 'loading' | 'success' | 'error' | 'info' = 'loading'): void {
    switch (type) {
      case 'success':
        toast.success(message, { id: toastId });
        break;
      case 'error':
        toast.error(message, { id: toastId });
        break;
      case 'info':
        toast(message, { id: toastId, icon: 'ℹ️' });
        break;
      default:
        toast.loading(message, { id: toastId });
    }
  }

  /**
   * API-specific error handling
   */
  handleApiError(error: any, context: string = 'Operation'): string {
    console.error(`${context} error:`, error);

    if (error?.response?.status === 401) {
      return this.permissionDenied('Please log in to continue');
    }

    if (error?.response?.status === 403) {
      return this.permissionDenied();
    }

    if (error?.response?.status === 429) {
      return this.rateLimit();
    }

    if (error?.response?.status >= 500) {
      return this.error('Something went wrong on our end. Please try again.');
    }

    if (error?.code === 'NETWORK_ERROR' || !error?.response) {
      return this.networkError('Can\'t reach our servers. Check your internet connection.');
    }

    const message = error?.response?.data?.message || error?.message || `${context} failed`;
    return this.error(message);
  }
}

// Export singleton instance
export const toastService = new ToastService();
export default toastService;
